<template>
  <div>
    <PageHeader :title="isEdit ? 'Edit Contact' : 'Add Contact'" showBack />

    <div class="card p-4">
      <b-form @submit.prevent="saveContact">
        <b-row>
          <b-col md="6">
            <b-form-group label="First Name" label-for="first_name">
              <b-form-input id="first_name" v-model="form.first_name" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Last Name" label-for="last_name">
              <b-form-input id="last_name" v-model="form.last_name" required />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Email" label-for="email">
              <b-form-input id="email" v-model="form.email" type="email" placeholder="email@example.com" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Phone" label-for="phone">
              <b-form-input id="phone" v-model="form.phone" placeholder="Phone number" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="Type" label-for="contact_type">
              <b-form-select id="contact_type" v-model="form.contact_type" :options="typeOptions" required />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Source" label-for="source">
              <b-form-select id="source" v-model="form.source" :options="sourceOptions" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group :label="stageLabel" label-for="stage">
              <b-form-select id="stage" v-model="form[stageField]" :options="stageOptions">
                <template #first>
                  <b-form-select-option :value="null">-- Select --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Program" label-for="program_category">
              <b-form-select id="program_category" v-model="form.program_category" :options="programOptions">
                <template #first>
                  <b-form-select-option :value="null">-- No Program --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <h6 class="text-uppercase text-muted mt-4 mb-3">Address</h6>
        <b-row>
          <b-col md="12">
            <b-form-group label="Street" label-for="address">
              <b-form-input id="address" v-model="form.address" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="5">
            <b-form-group label="City" label-for="city">
              <b-form-input id="city" v-model="form.city" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="State" label-for="state">
              <b-form-input id="state" v-model="form.state" />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Zip" label-for="zipcode">
              <b-form-input id="zipcode" v-model="form.zipcode" />
            </b-form-group>
          </b-col>
        </b-row>

        <h6 class="text-uppercase text-muted mt-4 mb-3">Notes</h6>
        <b-form-group label-for="notes">
          <b-form-textarea id="notes" v-model="form.notes" rows="3" />
        </b-form-group>

        <h6 class="text-uppercase text-muted mt-4 mb-3">Subscriptions</h6>
        <b-row>
          <b-col md="6">
            <b-form-checkbox v-model="form.is_subscribed_email" switch>Email subscribed</b-form-checkbox>
          </b-col>
          <b-col md="6">
            <b-form-checkbox v-model="form.is_subscribed_sms" switch>SMS subscribed</b-form-checkbox>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end mt-4">
          <b-button variant="outline-secondary" class="mr-2" :to="cancelRoute">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="isSaving">
            <b-spinner v-if="isSaving" small class="mr-1" />{{ isEdit ? 'Update' : 'Create' }}
          </b-button>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import {
  CONTACT_TYPES, CONTACT_SOURCES,
  LEAD_STAGES, STUDENT_STAGES, EMPLOYER_STAGES, OTHER_STAGES,
} from '../../common/constants';
import PageHeader from '../../components/Ui/PageHeader.vue';

const STAGE_FIELD_BY_TYPE = {
  lead: 'lead_stage',
  student: 'student_stage',
  employer: 'employer_stage',
  other: 'other_stage',
};

const STAGE_OPTIONS_BY_TYPE = {
  lead: LEAD_STAGES,
  student: STUDENT_STAGES,
  employer: EMPLOYER_STAGES,
  other: OTHER_STAGES,
};

export default {
  name: 'PostContact',
  components: { PageHeader },
  data() {
    return {
      isSaving: false,
      programs: [],
      form: this.getEmptyForm(),
      typeOptions: CONTACT_TYPES,
      sourceOptions: CONTACT_SOURCES,
    };
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },
    stageField() {
      return STAGE_FIELD_BY_TYPE[this.form.contact_type] || 'other_stage';
    },
    stageLabel() {
      const t = this.form.contact_type || 'other';
      return `${t.charAt(0).toUpperCase() + t.slice(1)} Stage`;
    },
    stageOptions() {
      return STAGE_OPTIONS_BY_TYPE[this.form.contact_type] || OTHER_STAGES;
    },
    programOptions() {
      return this.programs.map(p => ({ value: p.id, text: p.title }));
    },
    cancelRoute() {
      return this.isEdit
        ? { name: 'contact-detail', params: { id: this.$route.params.id } }
        : { name: 'contacts-list' };
    },
  },
  methods: {
    ...mapActions('contact', ['getContact', 'createContact', 'updateContact', 'getSchoolOfferedPrograms']),
    getEmptyForm() {
      return {
        first_name: '', last_name: '', email: '', phone: '',
        contact_type: 'lead', source: 'manual',
        lead_stage: null, student_stage: null, employer_stage: null, other_stage: null,
        program_category: null, school: null,
        address: '', city: '', state: '', zipcode: '', notes: '',
        is_subscribed_email: true, is_subscribed_sms: true,
      };
    },
    async loadContact() {
      if (!this.isEdit) return;
      try {
        const data = await this.getContact(this.$route.params.id);
        const c = data.data || data;
        const empty = this.getEmptyForm();
        Object.keys(empty).forEach(k => {
          if (c[k] !== undefined && c[k] !== null) this.form[k] = c[k];
        });
      } catch (err) {
        this.$bvToast && this.$bvToast.toast('Failed to load contact', { variant: 'danger', solid: true });
      }
    },
    async loadPrograms() {
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        if (user && user.school) {
          const res = await this.getSchoolOfferedPrograms({ school: user.school.id });
          this.programs = res.data || res || [];
        }
      } catch (err) {
        // silent
      }
    },
    buildPayload() {
      const payload = { ...this.form };
      const activeStageField = this.stageField;
      ['lead_stage', 'student_stage', 'employer_stage', 'other_stage'].forEach(f => {
        if (f !== activeStageField) delete payload[f];
        else if (!payload[f]) delete payload[f];
      });
      ['email', 'phone', 'address', 'city', 'state', 'zipcode', 'notes'].forEach(f => {
        if (payload[f] === '') delete payload[f];
      });
      if (payload.program_category === null) delete payload.program_category;
      return payload;
    },
    async saveContact() {
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        const payload = this.buildPayload();
        if (this.isEdit) {
          await this.updateContact({ id: this.$route.params.id, data: payload });
          this.$bvToast && this.$bvToast.toast('Contact updated', { variant: 'success', solid: true });
          this.$router.push({ name: 'contact-detail', params: { id: this.$route.params.id } });
        } else {
          const user = this.$store.getters['auth/getLoggedInUser'];
          if (user && user.school) payload.school = user.school.id;
          const res = await this.createContact(payload);
          const created = res.data || res;
          this.$bvToast && this.$bvToast.toast('Contact created', { variant: 'success', solid: true });
          if (created && created.id) {
            this.$router.push({ name: 'contact-detail', params: { id: created.id } });
          } else {
            this.$router.push({ name: 'contacts-list' });
          }
        }
      } catch (err) {
        const data = err.response && err.response.data;
        let msg = 'Save failed';
        if (data) {
          if (typeof data === 'string') msg = data;
          else if (data.detail) msg = data.detail;
          else {
            const first = Object.entries(data)[0];
            if (first) msg = `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}`;
          }
        }
        this.$bvToast && this.$bvToast.toast(msg, { variant: 'danger', solid: true });
      } finally {
        this.isSaving = false;
      }
    },
  },
  created() {
    this.loadPrograms();
    this.loadContact();
  },
};
</script>
